import { HydratedRouter } from 'react-router/dom';
import { StrictMode, startTransition } from 'react';
import { hydrateRoot } from 'react-dom/client';

import { SENTRY_ENABLED_ENVS, sentryBaseOption } from 'config';
import { SentryClient } from './lib/sentry.client';

const TEXT_MAX_LENGTH = 100;
const UI_CATEGORIES = ['ui.click', 'ui.input'];
const INTERACTIVE_ELEMENTS = ['BUTTON', 'INPUT', 'A'];
const TEXT_INPUT_TYPES = ['text', 'email', 'password', 'tel', 'url', 'search'];

SentryClient.init({
  ...sentryBaseOption,
  enabled: SENTRY_ENABLED_ENVS.includes(window.PUBLIC_ENV.envName),
  environment: window.PUBLIC_ENV.envName,
  integrations: [SentryClient.reactRouterTracingIntegration()],
  tracesSampleRate: 0.5,
  beforeBreadcrumb(breadcrumb, hint) {
    if (UI_CATEGORIES.includes(breadcrumb.category || '') && hint?.event) {
      const event = hint.event as Event;
      const target = event.target as HTMLElement | null;

      if (!target) {
        return breadcrumb;
      }

      const tagName = target.tagName;
      const isInteractiveElement = INTERACTIVE_ELEMENTS.includes(tagName);
      const inputType = target.getAttribute('type')?.toLowerCase() ?? 'text';

      // Only inputs that accept text
      const isTextInput =
        tagName === 'TEXTAREA' || (tagName === 'INPUT' && TEXT_INPUT_TYPES.includes(inputType));

      const ariaLabel = target.getAttribute('aria-label');

      // Only capture text content for interactive elements (buttons, inputs, links)
      // This avoids capturing PII from parent containers
      let text = ariaLabel || '';

      if (isInteractiveElement && !isTextInput) {
        // For buttons and links, capture text but limit length
        const textContent = target.textContent.trim().slice(0, TEXT_MAX_LENGTH);
        const innerText = target.innerText.trim().slice(0, TEXT_MAX_LENGTH);
        text = textContent || innerText;
      }

      // For inputs and buttons, get the type attribute
      const type = target.getAttribute('type');

      const id = target.id;

      const role = target.getAttribute('role');

      const name = target.getAttribute('name');

      const className = target.className;

      breadcrumb.data = {
        ...breadcrumb.data,
        text,
        ariaLabel,
        id,
        role,
        name,
        type,
        className,
      };

      if (text) {
        breadcrumb.message = `${tagName} - ${text}`;
      }
    }

    return breadcrumb;
  },
});

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <HydratedRouter />
    </StrictMode>
  );
});
